/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * @typedef {Object} ShopifyCartResponse
 * @property {string} token - Cart token
 * @property {string|null} note - Cart note
 * @property {Object} attributes - Cart attributes
 * @property {number} original_total_price - Original total price in cents
 * @property {number} total_price - Total price in cents
 * @property {number} total_discount - Total discount in cents
 * @property {number} total_weight - Total weight in grams
 * @property {number} item_count - Number of items in cart
 * @property {Array} items - Array of cart line items
 * @property {boolean} requires_shipping - Whether cart requires shipping
 * @property {string} currency - Currency code
 * @property {number} items_subtotal_price - Items subtotal price in cents
 * @property {Array} cart_level_discount_applications - Applied cart-level discounts
 */

/**
 * @typedef {Object} ShopifyCartError
 * @property {number} status - HTTP status code
 * @property {string} message - Error message
 * @property {string} description - Error description
 */

/**
 * @typedef {Object} DiscountApplication
 * @property {string} type - Type of discount (discount_code, automatic, script)
 * @property {string} value - Discount value
 * @property {string} value_type - Value type (fixed_amount, percentage)
 * @property {string} allocation_method - How discount is allocated
 * @property {string} target_selection - What the discount targets
 * @property {string} target_type - Target type
 * @property {string} title - Discount title/code
 */

class DiscountInput extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLDivElement|null} */
    this.container = null;

    /** @type {HTMLInputElement|null} */
    this.input = null;

    /** @type {HTMLButtonElement|null} */
    this.button = null;

    /** @type {HTMLElement|null} */
    this.spinner = null;

    /** @type {HTMLElement|null} */
    this.buttonText = null;

    /** @type {HTMLElement|null} */
    this.messageEl = null;

    /** @type {HTMLElement|null} */
    this.appliedEl = null;

    /** @type {HTMLElement|null} */
    this.appliedList = null;

    /** @type {boolean} */
    this.isLoading = false;
  }

  connectedCallback() {
    this.initializeElements();
    this.bindEvents();
    this.loadAppliedDiscounts();
  }

  /**
   * Initialize DOM element references
   */
  initializeElements() {
    this.container = this.querySelector(".discount-input__container");
    this.input = this.querySelector(".discount-input__input");
    this.button = this.querySelector(".discount-input__button");
    this.spinner = this.querySelector(".discount-input__spinner");
    this.buttonText = this.querySelector(".discount-input__button-text");
    this.messageEl = this.querySelector(".discount-input__message");
    this.appliedEl = this.querySelector(".discount-input__applied");
    this.appliedList = this.querySelector(".discount-input__applied-list");
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.button) {
      this.button.addEventListener("click", this.handleButtonClick.bind(this));
    }

    if (this.input) {
      this.input.addEventListener("input", this.handleInput.bind(this));
      this.input.addEventListener("keydown", this.handleKeydown.bind(this));
    }

    // Listen for cart updates from other components
    document.documentElement.addEventListener(
      "cart:updated",
      this.handleCartUpdate.bind(this)
    );
  }

  /**
   * Handle button click
   * @param {Event} event - Click event
   */
  async handleButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.input || this.isLoading) return;

    const discountCode = this.input.value.trim();

    if (!discountCode) {
      this.showMessage(this.dataset.errorEmpty, "error");
      return;
    }

    await this.applyDiscount(discountCode);
  }

  /**
   * Handle form submission - removed since we're not using a form
   * @param {Event} event - Form submit event
   */
  async handleSubmit(event) {
    // This method is kept for backwards compatibility but no longer used
    event.preventDefault();
    await this.handleButtonClick(event);
  }

  /**
   * Handle input changes
   * @param {Event} event - Input event
   */
  handleInput(event) {
    if (this.messageEl && !this.messageEl.hidden) {
      this.hideMessage();
    }
  }

  /**
   * Handle keydown events
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeydown(event) {
    if (event.key === "Enter" && !this.isLoading) {
      event.preventDefault();
      event.stopPropagation();
      this.handleButtonClick(event);
    }
  }

  /**
   * Handle cart update events from other components
   * @param {CustomEvent} event - Cart update event
   */
  handleCartUpdate(event) {
    this.loadAppliedDiscounts();
  }

  /**
   * Apply discount code to cart
   * @param {string} discountCode - The discount code to apply
   */
  async applyDiscount(discountCode) {
    this.setLoading(true);
    this.hideMessage();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            discount: discountCode,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      /** @type {ShopifyCartResponse|ShopifyCartError} */
      const data = await response.json();

      if (this.isErrorResponse(data)) {
        this.handleError(data);
        return;
      }

      // Check if the discount was actually applied
      const wasApplied = this.checkDiscountApplied(data, discountCode);

      if (wasApplied) {
        this.clearInput();
        this.updateAppliedDiscounts(data);

        // Reload page if on cart page, otherwise dispatch refresh event
        if (this.isCartPage()) {
          window.location.reload();
        } else {
          // Dispatch cart refresh event
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", {
              bubbles: true,
            })
          );
        }
      } else {
        this.showMessage(this.dataset.errorInvalid, "error");
      }
    } catch (error) {
      console.error("Error applying discount:", error);
      this.showMessage(this.dataset.errorApply, "error");
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Remove discount code from cart
   * @param {string} discountCode - The discount code to remove
   */
  async removeDiscount(discountCode) {
    this.setLoading(true);

    try {
      // Get current cart to get all current discounts
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const currentCart = await cartResponse.json();

      // Get all current discount codes except the one we want to remove
      const currentDiscounts = this.extractDiscountCodes(currentCart);
      const updatedDiscounts = currentDiscounts.filter(
        (code) => code !== discountCode
      );

      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            discount: updatedDiscounts.join(","),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      /** @type {ShopifyCartResponse} */
      const data = await response.json();

      this.updateAppliedDiscounts(data);

      // Reload page if on cart page, otherwise dispatch refresh event
      if (this.isCartPage()) {
        window.location.reload();
      } else {
        // Dispatch cart refresh event
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          })
        );
      }
    } catch (error) {
      console.error("Error removing discount:", error);
      this.showMessage(this.dataset.errorRemove, "error");
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Check if response is an error response
   * @param {ShopifyCartResponse|ShopifyCartError} data - Response data
   * @returns {data is ShopifyCartError} True if error response
   */
  isErrorResponse(data) {
    return "status" in data && "message" in data;
  }

  /**
   * Handle error response from Shopify
   * @param {ShopifyCartError} error - Error response
   */
  handleError(error) {
    let message = this.dataset.errorApply;

    if (error.description) {
      message = error.description;
    } else if (error.message) {
      message = error.message;
    }

    this.showMessage(message, "error");
  }

  /**
   * Check if discount was actually applied to cart
   * @param {ShopifyCartResponse} cartData - Cart response data
   * @param {string} discountCode - Discount code that was attempted
   * @returns {boolean} True if discount was applied
   */
  checkDiscountApplied(cartData, discountCode) {
    const appliedCodes = this.extractDiscountCodes(cartData);
    return appliedCodes.includes(discountCode.toUpperCase());
  }

  /**
   * Extract discount codes from cart data
   * @param {ShopifyCartResponse} cartData - Cart response data
   * @returns {string[]} Array of applied discount codes
   */
  extractDiscountCodes(cartData) {
    const codes = [];

    if (cartData.discount_codes) {
      cartData.discount_codes.forEach((discount) => {
        if (discount.code && discount.applicable) {
          codes.push(discount.code.toUpperCase());
        }
      });
    }

    return codes;
  }

  /**
   * Load and display currently applied discounts
   */
  async loadAppliedDiscounts() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cartData = await response.json();
      this.updateAppliedDiscounts(cartData);
    } catch (error) {
      console.error("Error loading applied discounts:", error);
    }
  }

  /**
   * Update the display of applied discounts
   * @param {ShopifyCartResponse} cartData - Cart response data
   */
  updateAppliedDiscounts(cartData) {
    if (!this.appliedEl || !this.appliedList) return;

    const discountCodes = this.extractDiscountCodes(cartData);

    if (discountCodes.length === 0) {
      this.appliedEl.hidden = true;
      return;
    }

    this.appliedList.innerHTML = "";

    discountCodes.forEach((code) => {
      const discountEl = document.createElement("div");
      discountEl.className = "discount-input__applied-item";
      discountEl.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" class="discount-input__icon">
            <path d="M17.78 3.09C17.45 2.443 16.778 2 16 2h-5.165c-.535 0-1.046.214-1.422.593l-6.82 6.89c0 .002 0 .003-.002.003-.245.253-.413.554-.5.874L.738 8.055c-.56-.953-.24-2.178.712-2.737L9.823.425C10.284.155 10.834.08 11.35.22l4.99 1.337c.755.203 1.293.814 1.44 1.533z" fill-opacity=".55"></path>
            <path d="M10.835 2H16c1.105 0 2 .895 2 2v5.172c0 .53-.21 1.04-.586 1.414l-6.818 6.818c-.777.778-2.036.782-2.82.01l-5.166-5.1c-.786-.775-.794-2.04-.02-2.828.002 0 .003 0 .003-.002l6.82-6.89C9.79 2.214 10.3 2 10.835 2zM13.5 8c.828 0 1.5-.672 1.5-1.5S14.328 5 13.5 5 12 5.672 12 6.5 12.672 8 13.5 8z"></path>
          </svg>
          <span class="discount-input__applied-code">${code}</span>
          <button type="button" class="discount-input__remove" data-discount="${code}" aria-label="Remove discount ${code}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `;

      const removeBtn = discountEl.querySelector(".discount-input__remove");
      if (removeBtn) {
        removeBtn.addEventListener("click", () => this.removeDiscount(code));
      }

      this.appliedList.appendChild(discountEl);
    });

    this.appliedEl.hidden = false;
  }

  /**
   * Check if we're currently on the cart page
   * @returns {boolean} True if on cart page
   */
  isCartPage() {
    return (
      window.location.pathname === "/cart" ||
      window.location.pathname.endsWith("/cart") ||
      window.location.pathname.includes("/cart/")
    );
  }

  /**
   * Show message to user
   * @param {string} message - Message text
   * @param {'error'|'info'} type - Message type
   */
  showMessage(message, type = "info") {
    if (!this.messageEl) return;

    this.messageEl.textContent = message;
    this.messageEl.className = `discount-input__message discount-input__message--${type}`;
    this.messageEl.hidden = false;
  }
  /**
   * Hide message
   */
  hideMessage() {
    if (this.messageEl) {
      this.messageEl.hidden = true;
    }
  }

  /**
   * Clear the input field
   */
  clearInput() {
    if (this.input) {
      this.input.value = "";
    }
  }

  /**
   * Set loading state
   * @param {boolean} loading - Loading state
   */
  setLoading(loading) {
    this.isLoading = loading;

    if (this.button) {
      this.button.disabled = loading;
    }

    if (this.spinner && this.buttonText) {
      this.spinner.hidden = !loading;
      this.buttonText.hidden = loading;
    }

    if (this.input) {
      this.input.disabled = loading;
    }
  }
}

// Register the custom element
if ("customElements" in window) {
  customElements.define("discount-input", DiscountInput);
}

/* ------------------------------------------------------------------------------------------------
 * MOBILE MENU DRAWER -- accordion behaviour
 *
 * Paired with the <style> block in snippets/mobile-menu.liquid, which carries the reasoning for the
 * sticky open row and the instant collapse these two listeners assume. Edit either one and re-read
 * the other.
 *
 * Here rather than in app/scripts/ because assets/index.js is gitignored and absent from this
 * checkout, so rebuilding it would mean uploading a bundle whose contents can't be inspected. This
 * file is the theme's own documented hook and is already deferred on every page (theme.liquid:164).
 *
 * No re-entrancy guard is needed: an external script executes once, so the theme editor re-rendering
 * the header section cannot stack a second copy of these listeners.
 * ---------------------------------------------------------------------------------------------- */

/* Only one top-level section stays open at a time. Without this, opening a second section left the
   first expanded, so two or three long panels could stack up and the top level scrolled away -- the
   exact problem the sticky row was added to relieve.

   theme.js dispatches openable-element:open through triggerEvent, which sets bubbles: true, so one
   document listener catches every panel. Every <collapsible-content> under .drawer__content is a
   level-1 panel now that the level-2 accordion is gone, so no depth check is needed.

   Assigning open = false removes the [open] attribute (OpenableElement, theme.js:1401), which fires
   openable-element:close. Each ToggleButton listens for that document-wide and resets its own
   aria-expanded and plus/minus, so the closed row's control stays correct without being touched here.
   Only close events are emitted, so this can't re-enter, and assigning false to an already-closed
   panel removes nothing and fires nothing.

   The drawer is looked up per event rather than cached: the append-body branch of OpenableElement can
   replace this element with a clone of itself (theme.js:1380) when one with the same id already
   exists -- what happens on a theme-editor re-render. A cached node would survive that detached. */
document.addEventListener('openable-element:open', (event) => {
  const opened = event.target;

  if (!(opened instanceof HTMLElement) || opened.tagName !== 'COLLAPSIBLE-CONTENT') {
    return;
  }

  const drawer = document.getElementById('mobile-menu-drawer');

  if (!drawer || !drawer.contains(opened)) {
    return;
  }

  drawer.querySelectorAll('.drawer__content collapsible-content').forEach((panel) => {
    if (panel !== opened) {
      panel.open = false;
    }
  });
});

/* Collapsing a section left the scrollport wherever that panel had pushed it -- often a screen or two
   down -- so the top level came back mid-list with the first sections above the fold. This returns you
   to the top of the main menu, the only state the collapsed drawer has ever had reason to be in. The
   cart drawer resets its own scrollport the same way (theme.js:6348).

   Guarded on "nothing is open any more" so it fires for a real collapse but not for the exclusivity
   close above: when you tap a second section, [open] is already on the new panel by the time its open
   event -- and therefore the previous panel's close -- is dispatched (attributeChangedCallback,
   theme.js:1430), so the query below still finds it and the reset is skipped. Switching sections then
   reads as: old panel gone, new one in its place, no jump.

   scrollTop rather than scrollTo({ behavior: 'smooth' }): the panel is removed instantly by the
   height:0 !important rule in mobile-menu.liquid, so there is nothing left to smooth-scroll past --
   and an instant reset needs no prefers-reduced-motion branch. */
document.addEventListener('openable-element:close', (event) => {
  const closed = event.target;

  if (!(closed instanceof HTMLElement) || closed.tagName !== 'COLLAPSIBLE-CONTENT') {
    return;
  }

  const drawer = document.getElementById('mobile-menu-drawer');

  if (!drawer || !drawer.contains(closed)) {
    return;
  }

  if (drawer.querySelector('.drawer__content collapsible-content[open]')) {
    return;
  }

  const scrollport = drawer.querySelector('.drawer__content');

  if (scrollport) {
    scrollport.scrollTop = 0;
  }
});
